import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { People, AttachMoney, TrendingUp, Assessment } from '@mui/icons-material';
import CompactMetricCard from '../components/common/CompactMetricCard';
import apiClient from '../api/client';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title as ChartTitle, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, Tooltip, Legend);

interface HRAnalysisPageProps {
  startDate: string | null;
  endDate: string | null;
}

interface HRSummaryData {
  period: {
    start_date: string;
    end_date: string;
  };
  summary: {
    total_employees: number;
    active_employees: number;
    inactive_employees: number;
    maternity_leave: number;
    new_joiners: number;
    avg_ctc: number;
    max_ctc: number;
    min_ctc: number;
    median_ctc: number;
  };
  department_breakdown: {
    department: string;
    count: number;
    avg_ctc: number;
    median_ctc: number;
    max_ctc: number;
    min_ctc: number;
  }[];
}

interface Employee {
  id: number;
  employee_name: string;
  employee_id: string;
  department: string;
  designation: string;
  ctc: number;
  ctc_per_month: number;
  employment_status: string;
  joining_date: string;
}

interface DeptTimeseriesDataset {
  department: string;
  data: number[];
}

interface DeptTimeseriesResponse {
  period: { start_date: string; end_date: string };
  labels: string[];
  departments: string[];
  datasets: DeptTimeseriesDataset[];
}

const HRAnalysisPage: React.FC<HRAnalysisPageProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hrSummaryData, setHrSummaryData] = useState<HRSummaryData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [deptSeries, setDeptSeries] = useState<DeptTimeseriesResponse | null>(null);
  const showDeptTrend = false; // Temporarily hide Department-wise Total Salary Trend graph

  // Format 'YYYY-MM' to 'MMM-YY' (e.g., 2025-04 -> Apr-25) to match dashboard standard
  const formatMonthLabel = useCallback((ym: string) => {
    try {
      const [y, m] = ym.split('-').map(Number);
      const d = new Date(y, (m || 1) - 1, 1);
      return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }).replace(' ', '-');
    } catch {
      return ym;
    }
  }, []);

  const formatCurrency = (value: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const fetchHRSummary = useCallback(async () => {
    // Use default date range if not provided
    const start = startDate || '2025-04';
    const end = endDate || '2025-09';

    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/api/v1/delivery/hr/summary?start_date=${start}&end_date=${end}`);
      setHrSummaryData(response.data);
    } catch (err: any) {
      setError(`Failed to load HR summary data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const fetchEmployees = useCallback(async (filterType: string) => {
    const start = startDate || '2025-04';
    const end = endDate || '2025-09';

    try {
      setLoadingEmployees(true);
      let url = '';
      let title = '';

      switch (filterType) {
        case 'all':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}`;
          title = 'All Employees';
          break;
        case 'active':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&status=Active`;
          title = 'Active Employees';
          break;
        case 'new_joiners':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&new_joiners=true`;
          title = 'New Joiners (Period)';
          break;
        case 'maternity':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&status=Maternity Leave`;
          title = 'Maternity Leave';
          break;
        case 'inactive':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&status=Inactive`;
          title = 'Inactive Employees';
          break;
        case 'inactive_maternity':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&status=Inactive,Maternity Leave`;
          title = 'Inactive & Maternity Leave Employees';
          break;
        case 'highest_earner':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&highest_earner=true`;
          title = 'Highest CTC Employees';
          break;
        case 'lowest_earner':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&lowest_earner=true`;
          title = 'Lowest CTC Employees';
          break;
        case 'department':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&department=${encodeURIComponent(modalTitle)}`;
          title = modalTitle;
          break;
        case 'top_earners':
          url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&top_earners=true`;
          title = 'Top Earners';
          break;
        default:
          return;
      }

      const response = await apiClient.get(url);
      setEmployees(response.data.employees || []);
      setModalTitle(title);
      setModalOpen(true);
    } catch (err: any) {
      setError(`Failed to load employee data: ${err.message}`);
    } finally {
      setLoadingEmployees(false);
    }
  }, [startDate, endDate, modalTitle]);

  const fetchEmployeesByDepartment = useCallback(async (department: string) => {
    const start = startDate || '2025-04';
    const end = endDate || '2025-09';

    try {
      setLoadingEmployees(true);
      const url = `/api/v1/delivery/hr/employees?start_date=${start}&end_date=${end}&department=${encodeURIComponent(department)}`;
      const response = await apiClient.get(url);
      setEmployees(response.data.employees || []);
      setModalTitle(`${department} Department`);
      setModalOpen(true);
    } catch (err: any) {
      setError(`Failed to load department employees: ${err.message}`);
    } finally {
      setLoadingEmployees(false);
    }
  }, [startDate, endDate]);

  const fetchDeptTimeseries = useCallback(async () => {
    const start = startDate || '2025-04';
    const end = endDate || '2025-09';
    try {
      const res = await apiClient.get(`/api/v1/delivery/hr/ctc-history/department-timeseries?start_date=${start}&end_date=${end}`);
      setDeptSeries(res.data);
    } catch (err: any) {
      setError(`Failed to load department salary trend: ${err.message}`);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchHRSummary();
    fetchDeptTimeseries();
  }, [startDate, endDate, fetchHRSummary, fetchDeptTimeseries]);

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 320, sm: 400 },
        }}
      >
        <CircularProgress size={60} />
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            color: 'text.secondary',
            fontWeight: 700,
            fontSize: 'clamp(0.95rem, 1vw, 1.05rem)',
          }}
        >
          Loading HR data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', position: 'relative' }}>
      {/* Summary Cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={6} sx={{ '@media (min-width: 1400px)': { flexBasis: '25%', maxWidth: '25%' } }}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => fetchEmployees('all')}>
            <CompactMetricCard
              title="Total Employees"
              value={hrSummaryData?.summary.total_employees.toString() || "0"}
              subtitle="All employees"
              icon={People}
              color="primary"
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={6} sx={{ '@media (min-width: 1400px)': { flexBasis: '25%', maxWidth: '25%' } }}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => fetchEmployees('active')}>
            <CompactMetricCard
              title="Active Employees"
              value={hrSummaryData?.summary.active_employees.toString() || "0"}
              subtitle="Currently working"
              icon={People}
              color="success"
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={6} sx={{ '@media (min-width: 1400px)': { flexBasis: '25%', maxWidth: '25%' } }}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => fetchEmployees('new_joiners')}>
            <CompactMetricCard
              title="New Joiners"
              value={hrSummaryData?.summary.new_joiners.toString() || "0"}
              subtitle={`Period`}
              icon={Assessment}
              color="info"
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={6} sx={{ '@media (min-width: 1400px)': { flexBasis: '25%', maxWidth: '25%' } }}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => fetchEmployees('inactive_maternity')}>
            <CompactMetricCard
              title="Inactive & Leave"
              value={((hrSummaryData?.summary.inactive_employees || 0) + (hrSummaryData?.summary.maternity_leave || 0)).toString()}
              subtitle={`${hrSummaryData?.summary.inactive_employees || 0} inactive, ${hrSummaryData?.summary.maternity_leave || 0} on leave`}
              icon={Assessment}
              color="warning"
            />
          </Box>
        </Grid>

        {/* Financial Cards */}
        <Grid item xs={12} sm={6} md={6} sx={{ '@media (min-width: 1400px)': { flexBasis: '25%', maxWidth: '25%' } }}>
          <CompactMetricCard
            title="Avg CTC"
            value={hrSummaryData?.summary.avg_ctc ? formatCurrency(hrSummaryData.summary.avg_ctc) : "₹0"}
            subtitle="Annual average"
            icon={AttachMoney}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} sx={{ '@media (min-width: 1400px)': { flexBasis: '25%', maxWidth: '25%' } }}>
          <CompactMetricCard
            title="Median CTC"
            value={hrSummaryData?.summary.median_ctc ? formatCurrency(hrSummaryData.summary.median_ctc) : "₹0"}
            subtitle="Middle value"
            icon={TrendingUp}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} sx={{ '@media (min-width: 1400px)': { flexBasis: '25%', maxWidth: '25%' } }}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => fetchEmployees('highest_earner')}>
            <CompactMetricCard
              title="Highest CTC"
              value={hrSummaryData?.summary.max_ctc ? formatCurrency(hrSummaryData.summary.max_ctc) : "₹0"}
              subtitle="Top earners"
              icon={AttachMoney}
              color="error"
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={6} sx={{ '@media (min-width: 1400px)': { flexBasis: '25%', maxWidth: '25%' } }}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => fetchEmployees('lowest_earner')}>
            <CompactMetricCard
              title="Lowest CTC"
              value={hrSummaryData?.summary.min_ctc ? formatCurrency(hrSummaryData.summary.min_ctc) : "₹0"}
              subtitle="Entry level"
              icon={TrendingUp}
              color="info"
            />
          </Box>
        </Grid>
      </Grid>

      {/* Department-wise Analysis */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 1.25, sm: 1.75 },
                fontWeight: 700,
                color: 'text.primary',
                fontSize: 'clamp(0.95rem, 1vw, 1.05rem)',
              }}
            >
              Department-wise Salary Analysis
            </Typography>
            <TableContainer
              sx={{
                width: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Table
                sx={{
                  minWidth: { xs: 980, sm: 980, md: '100%' },
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' },
                    py: { xs: 0.75, sm: 1 },
                    px: { xs: 1.25, sm: 1.5 },
                  },
                  '& .MuiTableCell-head': {
                    fontSize: { xs: '0.78rem', sm: '0.82rem', md: '0.78rem' },
                    fontWeight: 700,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>Department</TableCell>
                    <TableCell sx={{ color: 'text.secondary', textAlign: 'right' }}>Employees</TableCell>
                    <TableCell sx={{ color: 'text.secondary', textAlign: 'right' }}>Avg CTC</TableCell>
                    <TableCell sx={{ color: 'text.secondary', textAlign: 'right' }}>Median CTC</TableCell>
                    <TableCell sx={{ color: 'text.secondary', textAlign: 'right' }}>Max CTC</TableCell>
                    <TableCell sx={{ color: 'text.secondary', textAlign: 'right' }}>Min CTC</TableCell>
                    <TableCell sx={{ color: 'text.secondary', textAlign: 'right' }}>Total Salary</TableCell>
                    <TableCell sx={{ color: 'text.secondary', textAlign: 'right' }}>% of Total</TableCell>
                    <TableCell sx={{ color: 'text.secondary', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hrSummaryData?.department_breakdown.map((dept, index) => {
                    const totalSalary = hrSummaryData.department_breakdown.reduce((sum, d) => sum + (d.avg_ctc * d.count), 0);
                    const deptSalary = dept.avg_ctc * dept.count;
                    const percentage = totalSalary > 0 ? (deptSalary / totalSalary * 100) : 0;

                    return (
                      <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {dept.department}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', color: 'text.primary' }}>
                          {dept.count}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', color: 'primary.main', fontWeight: 500 }}>
                          {formatCurrency(dept.avg_ctc)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', color: 'success.main', fontWeight: 500 }}>
                          {formatCurrency(dept.median_ctc || 0)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', color: 'error.main', fontWeight: 500 }}>
                          {formatCurrency(dept.max_ctc)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', color: 'warning.main', fontWeight: 500 }}>
                          {formatCurrency(dept.min_ctc)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatCurrency(deptSalary)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', color: 'text.secondary' }}>
                          {percentage.toFixed(1)}%
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => fetchEmployeesByDepartment(dept.department)}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 2,
                              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.75rem' },
                              px: { xs: 1.25, sm: 1.5 },
                              py: { xs: 0.35, sm: 0.5 },
                              minWidth: { xs: 64, sm: 72 },
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {showDeptTrend && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.25, sm: 1.75 },
                  fontSize: 'clamp(0.95rem, 1vw, 1.05rem)',
                }}
              >
                Department-wise Total Salary Trend
              </Typography>
              {deptSeries && deptSeries.labels.length > 0 ? (
                <Box sx={{ height: { xs: 300, sm: 360, md: 400 } }}>
                  <Line
                    data={{
                      labels: deptSeries.labels.map((l) => formatMonthLabel(l)),
                      datasets: deptSeries.datasets.map((ds, idx) => {
                        const colors = [
                          '#1e40af', '#059669', '#dc2626', '#f59e0b', '#10b981', '#6b7280'
                        ];
                        const c = colors[idx % colors.length];
                        return {
                          label: ds.department,
                          data: ds.data,
                          borderColor: c,
                          backgroundColor: c + '33',
                          borderWidth: 3,
                          tension: 0.35,
                          fill: false,
                          pointRadius: 3
                        } as const;
                      })
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top' as const,
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                          }
                        },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          callbacks: {
                            label: (ctx) => {
                              const v = ctx.parsed.y as number;
                              return `${ctx.dataset.label}: ${formatCurrency(v)}`;
                            }
                          }
                        }
                      },
                      scales: {
                        x: {
                          grid: {
                            display: true,
                            color: 'rgba(0, 0, 0, 0.1)',
                          }
                        },
                        y: {
                          grid: {
                            display: true,
                            color: 'rgba(0, 0, 0, 0.1)',
                          },
                          ticks: {
                            callback: (value) => formatCurrency(value as number)
                          }
                        }
                      },
                      interaction: {
                        intersect: false,
                        mode: 'nearest',
                      },
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{
                  height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'action.hover', borderRadius: 2, border: '1px dashed', borderColor: 'divider'
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '0.9rem' } }}>
                      No Trend Data
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Employee Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <People color="primary" />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: 'clamp(0.95rem, 1vw, 1.05rem)',
              }}
            >
              {modalTitle}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: { xs: 1.5, sm: 2 } }}>
          {loadingEmployees ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' },
                    py: { xs: 0.75, sm: 1 },
                    px: { xs: 1.25, sm: 1.5 },
                  },
                  '& .MuiTableCell-head': {
                    fontSize: { xs: '0.78rem', sm: '0.82rem', md: '0.78rem' },
                    fontWeight: 700,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Employee ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>CTC/Month</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>CTC (Annual)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Joining Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow key={employee.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {employee.employee_name}
                      </TableCell>
                      <TableCell>{employee.employee_id}</TableCell>
                      <TableCell>{employee.department}</TableCell>
                      <TableCell>{employee.designation}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                        {formatCurrency(employee.ctc_per_month)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {formatCurrency(employee.ctc)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={employee.employment_status}
                          color={
                            employee.employment_status === 'Active' ? 'success' :
                              employee.employment_status === 'Maternity Leave' ? 'warning' : 'error'
                          }
                          size="small"
                          sx={{
                            fontSize: { xs: '0.72rem', sm: '0.75rem', md: '0.72rem' },
                            height: { xs: 18, sm: 20 },
                            '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(employee.joining_date).toLocaleDateString('en-IN')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            onClick={() => setModalOpen(false)}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' },
              px: { xs: 1.75, sm: 2.25 },
              py: { xs: 0.75, sm: 0.9 },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HRAnalysisPage;